import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, ShieldAlert, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { fmtDate, fmtTime, fmtDateTime } from '../approvals/shared';

const API = `${window.__BACKEND_URL__}/api`;

const KIND = {
  admin_override: { label: 'Admin override', color: '#FBBF24', bg: 'rgba(245,158,11,0.15)' },
  admin_created: { label: 'Added by admin', color: '#93C5FD', bg: 'rgba(59,130,246,0.15)' },
  certified_for: { label: 'Certified by supervisor', color: '#2DD4BF', bg: 'rgba(45,212,191,0.15)' },
};

/** Per-period list of shifts approved outside the normal chain, grouped by worker. */
export default function OverridesTab({ token, period }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});

  const load = useCallback(async () => {
    setData(null);
    try {
      const r = await axios.get(`${API}/payroll/overrides`, {
        headers: { Authorization: `Bearer ${token}` }, params: { start_date: period.start, end_date: period.end } });
      setData(r.data);
    } catch (e) { setData({ total: 0, workers: [], kinds: {} }); }
  }, [token, period.start, period.end]);

  useEffect(() => { if (period.start && period.end) load(); }, [load, period.start, period.end]);

  if (!data) return <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#1E88E5' }} />;

  return (
    <div className="space-y-3" data-testid="overrides-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[['Total', data.total, '#fff'], ['Admin overrides', data.kinds?.admin_override || 0, KIND.admin_override.color],
          ['Added by admin', data.kinds?.admin_created || 0, KIND.admin_created.color], ['Certified by supervisor', data.kinds?.certified_for || 0, KIND.certified_for.color]].map(([l, n, c]) => (
          <div key={l} className="ht-card p-3">
            <p className="text-[10px] uppercase tracking-wide" style={{ color: '#64748B' }}>{l}</p>
            <p className="text-2xl font-bold" style={{ color: c }} data-testid={`overrides-stat-${l.toLowerCase().replace(/\s+/g, '-')}`}>{n}</p>
          </div>
        ))}
      </div>
      <p className="text-xs" style={{ color: '#A0AEC0' }}>
        Shifts that were approved without the worker's own certification in {period.start} → {period.end}. Workers flagged
        <span className="font-semibold" style={{ color: '#F87171' }}> repeat</span> have 3+ such shifts or over half their shifts — a coaching conversation about My Time → Certify usually fixes it.
      </p>
      {data.workers.length === 0 ? (
        <div className="ht-card p-6 text-center" data-testid="overrides-empty">
          <ShieldAlert className="w-6 h-6 mx-auto mb-2" style={{ color: '#64748B' }} />
          <p className="text-sm" style={{ color: '#A0AEC0' }}>No overrides in this period — every approved shift was certified by its worker.</p>
        </div>
      ) : data.workers.map((w) => (
        <div key={w.worker_id} className="ht-card" data-testid={`overrides-worker-${w.worker_id}`}>
          <button onClick={() => setOpen((o) => ({ ...o, [w.worker_id]: !o[w.worker_id] }))}
            className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left" data-testid={`overrides-toggle-${w.worker_id}`}>
            <div className="min-w-0">
              <p className="text-sm text-white font-semibold flex items-center gap-2 flex-wrap">
                {w.worker_name}
                {w.repeat_offender && (
                  <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-semibold" style={{ background: 'rgba(239,68,68,0.15)', color: '#F87171' }} data-testid={`overrides-repeat-${w.worker_id}`}>
                    <AlertTriangle className="w-3 h-3" /> REPEAT
                  </span>
                )}
              </p>
              <p className="text-[11px]" style={{ color: '#A0AEC0' }}>
                {w.count} of {w.closed_shifts} shift{w.closed_shifts === 1 ? '' : 's'}{w.rate_pct != null ? ` (${w.rate_pct}%)` : ''}
                {w.admin_override ? ` · ${w.admin_override} override` : ''}{w.admin_created ? ` · ${w.admin_created} added` : ''}{w.certified_for ? ` · ${w.certified_for} certified-for` : ''}
              </p>
            </div>
            {open[w.worker_id] ? <ChevronUp className="w-4 h-4" style={{ color: '#A0AEC0' }} /> : <ChevronDown className="w-4 h-4" style={{ color: '#A0AEC0' }} />}
          </button>
          {open[w.worker_id] && (
            <div className="px-4 pb-3 space-y-1.5">
              {w.entries.map((e) => {
                const k = KIND[e.kind] || KIND.admin_override;
                return (
                  <div key={e.id} className="rounded-lg px-3 py-2" style={{ background: '#0B1321', border: '1px solid #2D3748' }} data-testid={`overrides-entry-${e.id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <p className="text-xs text-white font-semibold">{fmtDate(e.clock_in)} · {fmtTime(e.clock_in)} → {e.clock_out ? fmtTime(e.clock_out) : '—'} · {e.hours ?? '—'} h</p>
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: k.bg, color: k.color }}>{k.label}</span>
                    </div>
                    <p className="text-[11px] mt-1" style={{ color: '#A0AEC0' }}>
                      {e.by ? `${e.by}` : 'Unknown'}{e.at ? ` · ${fmtDateTime(e.at)}` : ''}{e.from_status ? ` · was "${e.from_status}"` : ''}
                    </p>
                    {e.reason && <p className="text-[11px] mt-0.5 italic" style={{ color: '#CBD5E1' }}>“{e.reason}”</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
